import {WINGMAN_BFF_ROOT, type WingmanBffKind} from "@/services/wingman/bff";
import {trimTrailingSlash, wingmanLegacyBaseUrl, wingmanV2BaseUrl} from "@/services/wingman/urls";

/**
 * Returns true when `url` points at a first-party Wingman API host, i.e. an
 * artifact Wingman minted (generated files, legacy artifacts) as opposed to a
 * cross-origin signed URL (e.g. GCS).
 *
 * Fetch sites do not send the parent-domain cookie to that host anymore;
 * they map the URL onto the same-origin rewrite with
 * {@link sameOriginArtifactUrl}. This predicate still answers the questions
 * that depend on provenance: whether a 401 says something about *our*
 * session (refresh-and-retry) and whether a link should use the blob
 * downloader. A third-party 401, e.g. an expired GCS signed URL, must not
 * trigger an Auth0 refresh.
 *
 * Only same-host comparison is used: subdomain matches are intentionally
 * NOT trusted. Both configured Wingman hosts are trusted — either can issue
 * artifact URLs.
 */
export const isFirstPartyArtifactUrl = (url: string | undefined): boolean => {
   if (!url) return false;
   try {
      const urlHost = new URL(url).host;
      return wingmanBases().some(({base}) => urlHost === base.host);
   } catch {
      return false;
   }
};

const wingmanBases = (): Array<{kind: WingmanBffKind; base: URL}> => {
   const out: Array<{kind: WingmanBffKind; base: URL}> = [];
   for (const [kind, raw] of [
      ["legacy", wingmanLegacyBaseUrl()],
      ["v2", wingmanV2BaseUrl()],
   ] as const) {
      if (!raw) continue;
      try {
         out.push({kind, base: new URL(raw)});
      } catch {
         // Misconfigured env: treat as "no such host" rather than throwing
         // from a render path.
      }
   }
   return out;
};

/**
 * Map a first-party Wingman artifact URL onto the same-origin signed-in BFF,
 * `/api/wingman/{kind}/{path}`, so the browser sends the httpOnly session
 * cookie to *our* origin only and the route handler swaps it for
 * `Authorization: Bearer` before the request leaves for Wingman. Wingman
 * therefore never has to decrypt the cookie for downloads or previews, and
 * artifact URLs already stored in conversation history (which name the
 * Wingman host) keep working without a data migration.
 *
 * The route handler streams the upstream body, which Vercel exempts from the
 * 4.5 MB function payload cap, so artifact size is not a concern here.
 * Anything that is not a Wingman URL, or that sits outside the configured
 * base path (e.g. `/v2`), is returned unchanged.
 */
export const sameOriginArtifactUrl = (url: string): string => {
   let target: URL;
   try {
      target = new URL(url);
   } catch {
      return url;
   }
   for (const {kind, base} of wingmanBases()) {
      if (target.host !== base.host) continue;
      const prefix = `${trimTrailingSlash(base.pathname)}/`;
      if (!target.pathname.startsWith(prefix)) continue;
      const path = target.pathname.slice(prefix.length);
      if (!path) continue;
      return `${WINGMAN_BFF_ROOT}/${kind}/${path}${target.search}`;
   }
   return url;
};

/**
 * Promote `http://` to `https://` when the app is on HTTPS. Backends still
 * occasionally hand out http:// download URLs, which Chromium blocks as
 * mixed content for dangerous filetypes (xlsx, exe, zip, …).
 */
export const upgradeInsecureUrl = (url: string): string => {
   if (typeof window === "undefined") return url;
   if (window.location.protocol !== "https:") return url;
   return url.startsWith("http://") ? "https://" + url.slice("http://".length) : url;
};

/**
 * Last percent-decoded path segment of `url`. Falls back to `download.bin`
 * (with extension — bare names confuse OS save dialogs) on nameless or
 * malformed URLs.
 */
export const fileNameFromUrl = (url: string, fallback = "download.bin"): string => {
   try {
      const {pathname} = new URL(url);
      const last = pathname.split("/").findLast(Boolean);
      if (!last) return fallback;
      try {
         return decodeURIComponent(last);
      } catch {
         return last;
      }
   } catch {
      return fallback;
   }
};
